import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Chip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import PaymentsIcon from '@mui/icons-material/Payments';
import axios from 'axios';

const fmt = (n) => `S/ ${Number(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`;

export default function Gratificaciones() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [calcOpen, setCalcOpen] = useState(false);
  const [calcAnio, setCalcAnio] = useState(String(new Date().getFullYear()));
  const [calcMes, setCalcMes] = useState('JULIO');
  const [pagarOpen, setPagarOpen] = useState(false);
  const [pagarId, setPagarId] = useState(null);
  const [pagarFecha, setPagarFecha] = useState('');

  useEffect(() => {
    axios.get('/api/rrhh/empresas').then(r => {
      setEmpresas(Array.isArray(r.data) ? r.data : []);
      if (r.data?.length === 1) setEmpresaId(r.data[0].empresa_id);
    }).catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true); setError('');
    try {
      const r = await axios.get('/api/rrhh/gratificaciones', { params: { empresa_id: empresaId, periodo: periodo || undefined } });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, [empresaId, periodo]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleCalcular = async () => {
    setError(''); setSuccess('');
    try {
      const r = await axios.post('/api/rrhh/gratificaciones/calcular', { empresa_id: empresaId, anio: calcAnio, mes_pago: calcMes });
      setCalcOpen(false);
      setSuccess(`Gratificación calculada: ${r.data.calculados} trabajadores.`);
      setPeriodo(`${calcAnio}-${calcMes}`);
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al calcular'); }
  };

  const handlePagar = async () => {
    try {
      await axios.put(`/api/rrhh/gratificaciones/${pagarId}/pagar`, { fecha_pago: pagarFecha });
      setPagarOpen(false); setPagarId(null); setPagarFecha('');
      setSuccess('Gratificación marcada como pagada.'); cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al pagar'); }
  };

  const empresaObj = empresas.find(e => e.empresa_id === empresaId) || null;
  const totalGratif = rows.reduce((s, r) => s + Number(r.importe_gratif || 0), 0);
  const totalBonif = rows.reduce((s, r) => s + Number(r.bonif_extraordinaria || 0), 0);
  const totalNeto = rows.reduce((s, r) => s + Number(r.total || 0), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1200 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, color: '#222' }}>Gratificaciones</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper sx={{ p: 2, borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Autocomplete
              options={empresas} getOptionLabel={o => o?.razon_social || ''}
              value={empresaObj}
              onChange={(_, v) => setEmpresaId(v ? v.empresa_id : '')}
              isOptionEqualToValue={(o, v) => o.empresa_id === v.empresa_id}
              renderInput={p => <TextField {...p} label="Empresa" size="small" sx={{ minWidth: '16em' }} />}
              sx={{ minWidth: '16em' }}
            />
            <TextField label="Período (ej: 2025-JULIO)" size="small" value={periodo} onChange={e => setPeriodo(e.target.value)} sx={{ minWidth: '16em' }} />
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="outlined" startIcon={<CalculateIcon />} onClick={() => setCalcOpen(true)} disabled={!empresaId}>
              Calcular Gratificación
            </Button>
          </Box>
        </Paper>

        {rows.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`Gratificación: ${fmt(totalGratif)}`} color="primary" variant="outlined" />
            <Chip label={`Bonif. Extraordinaria: ${fmt(totalBonif)}`} color="secondary" variant="outlined" />
            <Chip label={`Total a Pagar: ${fmt(totalNeto)}`} color="success" />
          </Box>
        )}

        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Trabajador</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Período</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rem. Computable</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Meses</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Gratificación</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Bonif. Extra. (9%)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>Total</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Selecciona empresa y calcula para ver resultados
                    </TableCell></TableRow>
                  ) : rows.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{r.trabajador_nombre}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.num_doc}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" label={r.periodo} color={r.mes_pago === 'JULIO' ? 'primary' : 'secondary'} variant="outlined" /></TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(r.rem_computable)}</TableCell>
                      <TableCell align="center">{r.meses_computables}m {r.dias_computables}d</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(r.importe_gratif)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(r.bonif_extraordinaria)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#2e7d32' }}>{fmt(r.total)}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={r.estado} color={r.estado === 'PAGADO' ? 'success' : 'warning'} />
                      </TableCell>
                      <TableCell align="center">
                        {r.estado === 'PENDIENTE' && (
                          <Tooltip title="Confirmar pago">
                            <IconButton size="small" color="success" onClick={() => { setPagarId(r.id); setPagarFecha(''); setPagarOpen(true); }}>
                              <PaymentsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Dialog Calcular */}
      <Dialog open={calcOpen} onClose={() => setCalcOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight="bold">Calcular Gratificación</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Julio:</strong> Período Ene–Jun (pago primera quincena de julio)<br />
            <strong>Diciembre:</strong> Período Jul–Dic (pago primera quincena de diciembre)<br />
            Incluye bonificación extraordinaria del 9% (equivalente EsSalud)
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Año" size="small" value={calcAnio} onChange={e => setCalcAnio(e.target.value)} sx={{ minWidth: '16em' }} />
            <TextField select label="Mes de Gratificación" size="small" value={calcMes} onChange={e => setCalcMes(e.target.value)} sx={{ minWidth: '16em' }}>
              <MenuItem value="JULIO">Julio</MenuItem>
              <MenuItem value="DICIEMBRE">Diciembre</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalcOpen(false)}>Cancelar</Button>
          <Button variant="contained" startIcon={<CalculateIcon />} onClick={handleCalcular}>Calcular</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Pagar */}
      <Dialog open={pagarOpen} onClose={() => setPagarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight="bold">Confirmar Pago Gratificación</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Fecha de Pago" size="small" type="date" InputLabelProps={{ shrink: true }}
            value={pagarFecha} onChange={e => setPagarFecha(e.target.value)} sx={{ mt: 1, minWidth: '16em' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPagarOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handlePagar} disabled={!pagarFecha}>Confirmar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
